import { CommonModule } from '@angular/common';
import { Component, ComponentRef, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef, inject } from '@angular/core';

import { Fai, Faim, FaimPremium, FaimUnlimited, Plan, ProductFactory, QuoteModel } from '@bytel/bytel-sales';

import { ConfigurationService } from '@services/configuration.service';
import { MainCartService } from '@services/main-cart.service';
import { MixedCartService } from '@services/mixed-cart.service';

import { Subscription } from 'rxjs';

import { ProductMapping } from '../../../catalog/product-mapping';
import { NavigateToDirective } from '../../../common/directives/navigate-to.directive';
import { SubscriptionBlockDirective } from './subscription-block.directive';
import { SubscriptionComponent } from './subscription/subscription.component';

const SIMO_TYPE_ID = 'plan_simo';
const SOWO_TYPE_ID = 'plan_sowo';

@Component({
    selector: 'checkout-subscription-block',
    templateUrl: './subscription-block.component.html',
    styleUrl: 'subscription-block.component.scss',
    standalone: true,
    imports: [NavigateToDirective, SubscriptionBlockDirective, CommonModule],
})
export class SubscriptionBlockComponent implements OnInit, OnDestroy {
    @Input() public quoteIndex: number = 0;

    public subscription: Plan;
    public isFai: boolean;
    public cartQuoteSubscription: Subscription;
    public editUrl: string;
    public showDeleteButton: boolean = true;
    public showEdit: boolean = true;
    @ViewChild(SubscriptionBlockDirective, { static: true }) subscriptionHost: SubscriptionBlockDirective;

    private mixedCartService: MixedCartService = inject(MixedCartService);
    private cartService: MainCartService = inject(MainCartService);
    private configurationService: ConfigurationService = inject(ConfigurationService);

    public ngOnInit(): void {
        this.subscription = this._getQuote().getPrincipalProduct<Plan>('Plan');
        this.isFai = ProductFactory.Is(this.subscription, Fai);
        this._loadComponent();
        this.cartQuoteSubscription = this.cartService.cartQuoteSubscription.subscribe((quoteIndex) => {
            if (quoteIndex === this.quoteIndex) {
                this._updateOnQuoteChange();
            }
        });
        const isMixedCart = this.mixedCartService.isMixedCart(this.cartService.cartModel);
        const quote = this.cartService.getQuote(this.quoteIndex);
        this.showDeleteButton = !quote.isRenewal();
        const isBankingOffer = this.subscription.data?.banking_offer;
        const shouldEditMixedCart = isMixedCart && this.quoteIndex === 0;
        this.showEdit = quote.isRenewal() ? !this.isFai : (!this.isFai && !isBankingOffer) || shouldEditMixedCart;

        if (this.subscription.categories?.includes(SIMO_TYPE_ID) && this.subscription.wallurl === SOWO_TYPE_ID) {
            this.subscription.wallurl = SIMO_TYPE_ID;
        }
        this.editUrl = this.showEdit ? this._getWallUrl() : '';
    }

    public ngOnDestroy(): void {
        this.cartQuoteSubscription.unsubscribe();
    }

    public remove(): void {
        this.cartService.removeProduct(this.subscription, this.quoteIndex).subscribe();
    }

    private _getWallUrl(): string {
        const isFaim =
            ProductFactory.Is(this.subscription, Faim) ||
            ProductFactory.Is(this.subscription, FaimPremium) ||
            ProductFactory.Is(this.subscription, FaimUnlimited);
        let wallUrl: string = isFaim ? this.subscription.wallurl : this.subscription.data?.commercial_range;
        const phone = this._getQuote().getProductsByType('Phone')?.[0]?.data?.url_key ?? '';

        if (this._getQuote().isRenewal()) {
            wallUrl = 'plan_renew';
        }
        return this.configurationService.getData()?.global.wall[wallUrl].replace('$phone', phone);
    }

    private _getQuote(): QuoteModel {
        return this.cartService.getQuote(this.quoteIndex);
    }

    /**
     *
     * @private
     * @memberof SubscriptionBlockComponent
     */
    private _updateOnQuoteChange(): void {
        const subscription: Plan = this._getQuote().getPrincipalProduct<Plan>('Plan');
        if (subscription) {
            this.subscription = subscription;
            this.isFai = ProductFactory.Is(this.subscription, Fai);
            const isBankingOffer = this.subscription.data?.banking_offer;
            this.showEdit = this._getQuote().isRenewal() ? !this.isFai : !this.isFai && !isBankingOffer;
            this._loadComponent();
        }
    }

    /**
     *
     *
     * @private
     * @memberof SubscriptionBlockComponent
     */
    private _loadComponent(): void {
        const viewContainerRef: ViewContainerRef = this.subscriptionHost.viewContainerRef;
        viewContainerRef.clear();
        const component = ProductMapping.getComponent(this.subscription);
        const componentRef: ComponentRef<SubscriptionComponent> = viewContainerRef.createComponent(component);
        componentRef.instance.subscription = this.subscription;
    }
}
